/**
 * What a password-recovery address is carrying, decided here and nowhere else.
 *
 * Supabase can land a recovery link on us in more than one shape, and which shapes are possible
 * depends on how the mail was raised — not on what the application would prefer. The client is
 * configured with `detectSessionInUrl: false` (see `supabaseAdapter.ts`), so nothing is consumed
 * by accident: the address is read here, deliberately, and only what this function classifies as a
 * recovery grant is ever handed to the provider.
 *
 * The three consumable shapes, and where each comes from:
 *
 *   session     `#access_token=…&refresh_token=…&type=recovery`
 *               GoTrue's `/auth/v1/verify` redirect when the mail was raised WITHOUT a PKCE
 *               challenge — which is this installation's case, because nothing in this application
 *               calls `resetPasswordForEmail`. The recovery mail is raised by an administrator, so
 *               this is the shape to expect in production.
 *   token_hash  `?token_hash=…&type=recovery`
 *               the mail template's `{{ .TokenHash }}` form, answered with `verifyOtp`. Works from
 *               any browser, because nothing was stored locally when the mail was raised.
 *   code        `?code=…`
 *               the PKCE form. Supabase's own rule is that the code verifier is written when the
 *               flow is STARTED and the exchange must happen in that same browser; this
 *               application never starts one, so a `code` link cannot be completed here today. It
 *               is still classified — rather than read as «nothing» — so it fails closed with the
 *               link message instead of silently looking like an empty address.
 *
 * Two shapes are not grants and are named so the screen can refuse without guessing:
 *
 *   refused     the provider already said no: `error=access_denied`, `error_code=otp_expired`.
 *               GoTrue writes these into the fragment or the query depending on the flow, so both
 *               are read.
 *   absent      no recovery parameters at all — someone typed the address, or followed a link that
 *               has already been scrubbed. A normal session is NOT a recovery, so this is where an
 *               ordinary signed-in visitor lands.
 *
 * Nothing here is stored, logged or returned beyond the single value its own shape needs, and the
 * caller scrubs the address before the provider is asked anything.
 */

/** A recovery link that still has something to consume. */
export type RecoveryGrant =
  | { kind: 'code'; code: string }
  | { kind: 'token_hash'; tokenHash: string }
  | { kind: 'session'; accessToken: string; refreshToken: string };

/** Why an address carried no usable grant. */
export type RecoveryRefusal = 'expired' | 'denied' | 'unknown';

export type RecoveryLink = RecoveryGrant | { kind: 'refused'; reason: RecoveryRefusal } | { kind: 'absent' };

/** The two places GoTrue writes its parameters. Taken as strings so this stays pure. */
export interface RecoveryAddress {
  search: string;
  hash: string;
}

function paramsOf(address: RecoveryAddress): URLSearchParams[] {
  return [
    new URLSearchParams(address.search.replace(/^\?/, '')),
    new URLSearchParams(address.hash.replace(/^#/, '')),
  ];
}

/** First non-empty value for `name`, whichever half of the address carries it. */
function read(all: URLSearchParams[], name: string): string | null {
  for (const params of all) {
    const value = params.get(name);
    if (value !== null && value !== '') return value;
  }
  return null;
}

export function parseRecoveryLink(address: RecoveryAddress): RecoveryLink {
  const all = paramsOf(address);

  /*
   * A refusal wins over everything else. An expired link arrives with BOTH an error and, in some
   * flows, leftover parameters; reading the grant first would open a form on a link the provider
   * has already rejected.
   */
  const error = read(all, 'error');
  const errorCode = read(all, 'error_code');
  if (error !== null || errorCode !== null) {
    if (errorCode === 'otp_expired') return { kind: 'refused', reason: 'expired' };
    if (error === 'access_denied') return { kind: 'refused', reason: 'denied' };
    return { kind: 'refused', reason: 'unknown' };
  }

  /*
   * The type is checked rather than assumed. A sign-up confirmation, a magic link and an e-mail
   * change all land with the same parameter names; none of them is permission to set a password,
   * so anything that is not `recovery` is refused here instead of downstream.
   */
  const type = read(all, 'type');

  const accessToken = read(all, 'access_token');
  const refreshToken = read(all, 'refresh_token');
  if (accessToken !== null && refreshToken !== null) {
    if (type !== 'recovery') return { kind: 'refused', reason: 'denied' };
    return { kind: 'session', accessToken, refreshToken };
  }

  const tokenHash = read(all, 'token_hash');
  if (tokenHash !== null) {
    if (type !== 'recovery') return { kind: 'refused', reason: 'denied' };
    return { kind: 'token_hash', tokenHash };
  }

  /* PKCE carries no type of its own; the code is meaningless unless the provider issued it. */
  const code = read(all, 'code');
  if (code !== null) return { kind: 'code', code };

  return { kind: 'absent' };
}

/** True for the shapes `beginPasswordRecovery` can be asked to consume. */
export function isRecoveryGrant(link: RecoveryLink): link is RecoveryGrant {
  return link.kind === 'code' || link.kind === 'token_hash' || link.kind === 'session';
}
